import { memo, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useWatch } from "react-hook-form";
import { Grid } from "@mui/material";
import {
  AutoComplete,
  FormInput,
  SwitchInput,
  useFormContext,
} from "@4uhub/lib4uhub";
import MainApi from "../../../../services/mainApi.service";
import { ICompanyDigitalCertificateTypeDocument } from "../../../../models/company-digital-certificate";
import { TDigitalCertificateForm } from "./digitalCertificateSchema";
import {
  getCompanyDigitalCertificateDocumentDigits,
  getCompanyDigitalCertificateDocumentKind,
  getCompanyDigitalCertificateDocumentMask,
  getCompanyDigitalCertificateDocumentMaxLength,
} from "../digitalCertificateDocument";

const translationPath = "page.register.digital_certificates.";
const GENERIC_ROUTE = process.env.REACT_APP_MAIN + "/api/v1/Generic/";
const TYPE_DOCUMENT_PARAMS = {
  Identifier: "CompanyDigitalCertificateTypeDocument",
};

const typeDocumentService = new MainApi<ICompanyDigitalCertificateTypeDocument>(
  GENERIC_ROUTE,
);

const getTypeDocumentLabel = (option: ICompanyDigitalCertificateTypeDocument) =>
  option.value || option.name || option.code || "";

export const DigitalCertificateFields = memo(() => {
  const { t } = useTranslation();
  const { control, getValues, setValue } =
    useFormContext<TDigitalCertificateForm>();

  const typeDocument = useWatch({ control, name: "typeDocument" });

  const documentKind = useMemo(
    () => getCompanyDigitalCertificateDocumentKind({ typeDocument }),
    [typeDocument],
  );

  const documentMask = useMemo(
    () => getCompanyDigitalCertificateDocumentMask(documentKind),
    [documentKind],
  );

  const documentMaxLength = useMemo(
    () => getCompanyDigitalCertificateDocumentMaxLength(documentKind),
    [documentKind],
  );

  useEffect(() => {
    const currentValue = getValues("document") ?? "";
    const cleanValue = getCompanyDigitalCertificateDocumentDigits(currentValue);

    if (currentValue !== cleanValue) {
      setValue("document", cleanValue, {
        shouldDirty: false,
        shouldValidate: true,
      });
    }
  }, [documentKind, getValues, setValue]);

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} sm={12} textAlign="end">
        <SwitchInput name="enable" label={t(translationPath + "enable")} />
        <SwitchInput
          name="useCertillionLocalAgent"
          label={t(translationPath + "useCertillionLocalAgent")}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <FormInput
          name="name"
          label={t(translationPath + "name")}
          size="small"
          fullWidth
          autoFocus
        />
      </Grid>
      <Grid item xs={12} sm={3}>
        <AutoComplete
          params={TYPE_DOCUMENT_PARAMS}
          name="typeDocument"
          label={t(translationPath + "type_document")}
          getOptionLabel={getTypeDocumentLabel}
          fullWidth
          size="small"
          request={typeDocumentService.list}
        />
      </Grid>
      <Grid item xs={12} sm={3}>
        <FormInput
          key={`document-${documentKind ?? "default"}`}
          name="document"
          label={t(translationPath + "document")}
          size="small"
          fullWidth
          mask={documentMask}
          unmask={Boolean(documentMask)}
          inputProps={{ maxLength: documentMaxLength }}
        />
      </Grid>
    </Grid>
  );
});
